import React, { useRef, useEffect, useState, lazy, Suspense } from 'react';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import { useSelector } from 'react-redux';
import { mediaSelector, matrixSelector } from '../slices/matrixSlice';

const ReactPlayer = lazy(() => import('react-player/lazy'));

const Media = ({ productName }) => {
  const {  AveryCustom: { assetUrl } } = useSelector(matrixSelector);
  const media = useSelector(mediaSelector);
  const mainRef = useRef();
  const thumbsRef = useRef();
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);

  const syncGallery = () => {
    if (mainRef.current && thumbsRef.current && thumbsRef.current.splide) {
      mainRef.current.sync(thumbsRef.current.splide);
      mainRef.current.splide.go(0);
    }
  };

  useEffect(() => {
    syncGallery();
  }, [media]);

  const mainOptions = {
    type       : 'fade',
    rewind     : true,
    pagination : false,
    arrows     : false,
    fixedWidth : '100%',
    fixedHeight: '100%'
  };

  const thumbsOptions = {
    gap         : 15,
    rewind      : true,
    pagination  : false,
    isNavigation: true,
    mediaQuery  : 'min',
    arrows      : media.length > 5,
    perPage     : Math.min(5, media.length),
    breakpoints : {
      400: {
        fixedWidth: 62,
        fixedHeight: 62
      },
      420: {
        fixedWidth: 66,
        fixedHeight: 66
      },
      512: {
        fixedWidth: (media.length >= 5) ? '20%' : (100/media.length) + '%',
        fixedHeight: 'auto'
      },
      640: {
        fixedWidth: 56,
        fixedHeight: 56
      },
      820: {
        fixedWidth: 77,
        fixedHeight: 77
      },
      1030: {
        fixedWidth: 92,
        fixedHeight: 92
      }
    }
  };

  return (
    <>
      <Splide
        ref={mainRef}
        options={mainOptions}
        aria-label={productName}
        onReady={ (splide) => {
          splide.Components.Slides.forEach(slide => {
            if (slide.slide.getAttribute('aria-hidden') === 'true') {
              slide.slide.setAttribute('inert', '');
            }
          })
        }}
        onMoved={ ( splide, newIndex ) => {
          setCurrentMediaIndex(newIndex);
        }}
        onActive={ (splide, Slide) => {
          Slide.slide.removeAttribute('inert');
        }}
        onInactive={ (splide, Slide) => {
          Slide.slide.setAttribute('inert', '');
       }}
      >
        {
          media.map((item, i) => {
            return (
              <SplideSlide key={'main-' + i}>
                <Choose>
                  <When condition={item.bundle === 'video' && item.video_url}>
                    <div className="relative" style={{ paddingBottom: '56.25%', margin: '21.875% 0px' }}>
                      <Suspense fallback={<div className="bg-black w-full h-full absolute inset-0"></div>}>
                        <ReactPlayer
                          url={item.video_url}
                          playing={currentMediaIndex === i}
                          volume={0}
                          muted={true}
                          controls={true}
                          width="100%"
                          height=""
                          style={{
                            position: 'absolute',
                            top: '0',
                            right: '0',
                            bottom: '0',
                            left: '0',
                          }}
                        />
                      </Suspense>
                    </div>
                  </When>
                  <Otherwise>
                    <img
                      // Horrible hack for B2B
                      // assetUrl is supposed to be an empty string on DTC, so
                      // this should not hurt the DTC use case.
                      // Only the first image in our splide show is changing with
                      // the configuration - and it's onl the first images that
                      // are bound to change do to assortment changes.
                      // So we're only retrieving the first image from assetUrl
                      // and take the rest from the local file system. Sigh.
                      src={(i==0?assetUrl:'') + item.image}
                      alt={item.dynamic ? productName : item.alt}
                      width={item.image_width || undefined}
                      height={item.image_height || undefined}
                      fetchpriority={i === 0 ? 'high' : undefined}
                    />
                  </Otherwise>
                </Choose>
              </SplideSlide>
            );
          })
        }
      </Splide>
      <If condition={media.length > 1}>
        <div className="mt-sm">
          <Splide
            hasTrack={false}
            ref={thumbsRef}
            options={thumbsOptions}
            aria-label={productName}
            onReady={ (splide) => {
              splide.Components.Slides.forEach(slide => {
                if (slide.slide.getAttribute('aria-hidden') === 'true') {
                  slide.slide.setAttribute('inert', '');
                }
              })
            }}
            onVisible={ (splide, Slide) => {
              Slide.slide.removeAttribute('inert');
            }}
            onHidden={ (splide, Slide) => {
              Slide.slide.setAttribute('inert', '');
            }}
          >
            <SplideTrack>
            {
              media.map((item, i) => {
                return (
                    <SplideSlide key={'thumbnail-' + i}>
                      <If condition={item.bundle === 'video'}>
                        <ft-icon
                          color="social-youtube"
                          class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-social-youtube"
                          name="play"></ft-icon>
                      </If>
                      <img
                        loading="eager"
                        // Horrible hack. See above.
                        src={(i==0?assetUrl:'') + item.thumbnail}
                        alt={item.dynamic ? productName : item.alt}
                        width={item.thumbnail_width || undefined}
                        height={item.thumbnail_height || undefined}
                      />
                    </SplideSlide>
                );
              })
            }
            </SplideTrack>
          </Splide>
        </div>
      </If>
    </>
  );
};

export default React.memo(Media);
